import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export default function OwnerSettings() {
  const { currentBusiness, currentSettings, updateBusinessProfile, updateBusinessSettings, configError } = useApp();
  const [profile, setProfile] = useState({
    name: currentBusiness?.name ?? "",
    email: currentBusiness?.email ?? "",
    phone: currentBusiness?.phone ?? "",
    address: currentBusiness?.address ?? "",
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [settingsDraft, setSettingsDraft] = useState(() => ({
    cancellationWindowHours: currentSettings?.cancellationWindowHours ?? 24,
    requireDeposit: currentSettings?.requireDeposit ?? false,
    emailReminders: currentSettings?.emailReminders ?? true,
    smsReminders: currentSettings?.smsReminders ?? false,
    hours: currentSettings?.hours ?? {
      Monday: { open: "09:00", close: "17:00", closed: false },
      Tuesday: { open: "09:00", close: "17:00", closed: false },
      Wednesday: { open: "09:00", close: "17:00", closed: false },
      Thursday: { open: "09:00", close: "17:00", closed: false },
      Friday: { open: "09:00", close: "17:00", closed: false },
      Saturday: { open: "10:00", close: "15:00", closed: false },
      Sunday: { open: "10:00", close: "15:00", closed: true },
    },
  }));

  useEffect(() => {
    setProfile({
      name: currentBusiness?.name ?? "",
      email: currentBusiness?.email ?? "",
      phone: currentBusiness?.phone ?? "",
      address: currentBusiness?.address ?? "",
    });
  }, [currentBusiness]);

  useEffect(() => {
    if (!currentSettings) {
      return;
    }

    setSettingsDraft({
      cancellationWindowHours: currentSettings.cancellationWindowHours,
      requireDeposit: currentSettings.requireDeposit,
      emailReminders: currentSettings.emailReminders,
      smsReminders: currentSettings.smsReminders,
      hours: currentSettings.hours,
    });
  }, [currentSettings]);

  if (!currentSettings) {
    return null;
  }

  const saveProfile = async () => {
    setError("");
    setMessage("");
    if (!profile.name.trim() || !profile.email.trim()) {
      setError("Business name and email are required.");
      return;
    }
    if (!profile.email.includes("@")) {
      setError("Enter a valid business email.");
      return;
    }
    await updateBusinessProfile(profile);
    setMessage("Business profile saved.");
  };

  const saveSettings = async () => {
    setError("");
    setMessage("");
    if (settingsDraft.cancellationWindowHours < 0) {
      setError("Cancellation window cannot be negative.");
      return;
    }
    await updateBusinessSettings(settingsDraft);
    setMessage("Booking settings saved.");
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your business profile, hours, and booking preferences</p>
      </div>

      <section className="rounded-xl border bg-card p-6 shadow-soft space-y-4">
        <h2 className="font-heading font-semibold text-lg">Business Profile</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Business Name</Label><Input value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} className="mt-1" /></div>
          <div><Label>Email</Label><Input value={profile.email} onChange={(event) => setProfile({ ...profile, email: event.target.value })} className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} className="mt-1" /></div>
          <div><Label>Address</Label><Input value={profile.address} onChange={(event) => setProfile({ ...profile, address: event.target.value })} className="mt-1" /></div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {message && <p className="text-sm text-success">{message}</p>}
        <Button size="sm" onClick={() => void saveProfile()}>Save Changes</Button>
      </section>

      <section className="rounded-xl border bg-card p-6 shadow-soft space-y-4">
        <h2 className="font-heading font-semibold text-lg">Hours of Operation</h2>
        {days.map((day) => {
          const config = settingsDraft.hours[day];
          return (
            <div key={day} className="flex items-center justify-between py-2 border-b last:border-0">
              <span className="text-sm font-medium w-24">{day}</span>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!config.closed}
                  onChange={(event) => setSettingsDraft((prev) => ({
                    ...prev,
                    hours: {
                      ...prev.hours,
                      [day]: { ...config, closed: !event.target.checked },
                    },
                  }))}
                />
                <Input
                  value={config.open}
                  className="w-24 text-xs h-8"
                  disabled={config.closed}
                  onChange={(event) => setSettingsDraft((prev) => ({
                    ...prev,
                    hours: {
                      ...prev.hours,
                      [day]: { ...config, open: event.target.value },
                    },
                  }))}
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  value={config.close}
                  className="w-24 text-xs h-8"
                  disabled={config.closed}
                  onChange={(event) => setSettingsDraft((prev) => ({
                    ...prev,
                    hours: {
                      ...prev.hours,
                      [day]: { ...config, close: event.target.value },
                    },
                  }))}
                />
              </div>
            </div>
          );
        })}
        <Button size="sm" onClick={() => void saveSettings()}>Save Hours</Button>
      </section>

      <section className="rounded-xl border bg-card p-6 shadow-soft space-y-4">
        <h2 className="font-heading font-semibold text-lg">Policies & Notifications</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Cancellation Window</p><p className="text-xs text-muted-foreground">Minimum hours before appointment</p></div>
            <Input value={settingsDraft.cancellationWindowHours} onChange={(event) => setSettingsDraft((prev) => ({ ...prev, cancellationWindowHours: Number(event.target.value) }))} className="w-20 text-sm h-8" />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Require Deposit</p><p className="text-xs text-muted-foreground">Collect deposit at booking</p></div>
            <Switch checked={settingsDraft.requireDeposit} onCheckedChange={(checked) => setSettingsDraft((prev) => ({ ...prev, requireDeposit: checked }))} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Email Reminders</p><p className="text-xs text-muted-foreground">Send appointment reminders</p></div>
            <Switch checked={settingsDraft.emailReminders} onCheckedChange={(checked) => setSettingsDraft((prev) => ({ ...prev, emailReminders: checked }))} />
          </div>
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">SMS Reminders</p><p className="text-xs text-muted-foreground">Text message reminders</p></div>
            <Switch checked={settingsDraft.smsReminders} onCheckedChange={(checked) => setSettingsDraft((prev) => ({ ...prev, smsReminders: checked }))} />
          </div>
        </div>
        <Button size="sm" onClick={() => void saveSettings()}>Save Policies</Button>
      </section>
      {configError && (
        <section className="rounded-xl border bg-card p-6 shadow-soft space-y-2">
          <h2 className="font-heading font-semibold text-lg">Connection Status</h2>
          <p className="text-sm text-muted-foreground">{configError}</p>
        </section>
      )}
    </div>
  );
}
